import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Permission } from '@brandschutz/shared';
import { CollapsibleCard } from '@/components/ui/CollapsibleCard';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/lib/icons';
import { useCanI } from '@/lib/rbac';
import { formatDate } from '@/lib/format';
import { listScenes } from './api';
import { useSceneThumbnail } from './useSceneThumbnail';

interface Props {
  projectId: number;
  /**
   * Projekt gesperrt (abgeschlossen) oder kein Schreibrecht. Dann entfaellt
   * der Absprung in den Editor — vorhandene Szenen bleiben sichtbar, denn
   * Ansehen war nie das Problem.
   */
  readOnly?: boolean;
  /** Optional: Title-Override (default „Markup-Szenen"). */
  title?: string;
}

/**
 * Galerie-Sektion mit allen Markup-Szenen eines Projekts.
 * Wird im ProjectForm/ProjectDetail eingebettet, damit Markup-Grafiken
 * direkt im Formular sichtbar sind — nicht nur als separate Seite.
 *
 * Klick auf eine Karte öffnet den Markup-Editor in einem neuen Tab.
 * „Neue Szene"-Button springt direkt zum Wizard.
 */
export function MarkupGallery({ projectId, title = 'Markup-Szenen', readOnly }: Props) {
  // Der Editor-Route liegt PROJECT_UPDATE zugrunde. Nur-Leser sehen deshalb die
  // Galerie, aber keine Links dorthin — ein Link, der in „Kein Zugriff" endet,
  // wirkt wie ein Fehler der Anwendung. Dasselbe gilt fuer ein abgeschlossenes
  // Projekt: dort verweigert das Backend das Anlegen ohnehin.
  const canEdit = useCanI(Permission.PROJECT_UPDATE) && !readOnly;
  const query = useQuery({
    queryKey: ['markup-scenes-gallery', projectId],
    queryFn: () => listScenes(projectId),
    enabled: Number.isFinite(projectId) && projectId > 0,
  });

  const scenes = query.data ?? [];

  // Aus den ohnehin geladenen Szenen — zugeklappt sagt sie, ob überhaupt
  // Grundriss-Markup existiert.
  const zusammenfassung = query.isLoading
    ? 'Wird geladen …'
    : scenes.length === 0
      ? 'Noch keine Szene angelegt'
      : `${scenes.length} ${scenes.length === 1 ? 'Szene' : 'Szenen'}`;

  return (
    // Zugeklappt als Startzustand: die Galerie ist ein Absprung in den Editor
    // (eigene Route) — punktuell, und die Thumbnails sind der längste Block.
    <CollapsibleCard
      cardKey="projekt.markupszenen"
      title={title}
      summary={zusammenfassung}
      defaultCollapsed
      enlargeDescription="Brandschutz-Markup auf Grundrissen. Wird im Brandschutznachweis-Export als Anhang eingebettet."
      action={
        canEdit ? (
          <Link to={`/projects/${projectId}/markup`}>
            <Button size="sm" iconLeft={<Icon name="Plus" size={12} />}>
              Neue Szene
            </Button>
          </Link>
        ) : null
      }
    >
      <p className="mb-3 text-xs text-ink-500">
        Brandschutz-Markup auf Grundrissen. Wird im Brandschutznachweis-Export als Anhang
        eingebettet.
      </p>

      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton h-32 rounded" />
          ))}
        </div>
      ) : scenes.length === 0 ? (
        <div className="py-6 text-center">
          <Icon name="LayoutTemplate" size={24} className="mx-auto text-ink-400" />
          <p className="mt-1.5 text-xs text-ink-500">Noch keine Markup-Szenen.</p>
          {canEdit ? (
            <Link
              to={`/projects/${projectId}/markup`}
              className="mt-2 inline-flex items-center gap-1 text-xs text-accent-700 hover:underline"
            >
              <Icon name="Plus" size={11} /> Erste Szene anlegen
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {scenes.map((s) => {
            const tile = (
              <>
                <SceneThumbnail
                  sceneId={s.id}
                  label={s.label}
                  hasThumbnail={Boolean(s.thumbnailPath)}
                />
                <div className="border-t border-ink-100 p-2">
                  <p className="truncate text-xs font-medium text-ink-900">{s.label}</p>
                  <p className="text-[10px] text-ink-500">
                    {formatDate(s.updatedAt)}
                    {s.pxPerMeter ? ` · ${s.pxPerMeter.toFixed(1)} px/m` : null}
                  </p>
                </div>
              </>
            );
            const tileClass = 'block overflow-hidden rounded border border-ink-200 bg-paper';
            return canEdit ? (
              <Link
                key={s.id}
                to={`/projects/${projectId}/markup/${s.id}`}
                className={`group ${tileClass} transition-shadow hover:shadow-elev-2`}
              >
                {tile}
              </Link>
            ) : (
              <div key={s.id} className={tileClass}>
                {tile}
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleCard>
  );
}

/**
 * Thumbnail-Kachel. Der Endpoint braucht einen Bearer-Token, deshalb laden wir
 * die Bytes über den Hook und nicht direkt via `<img src>`.
 */
function SceneThumbnail({
  sceneId,
  label,
  hasThumbnail,
}: {
  sceneId: number;
  label: string;
  hasThumbnail: boolean;
}) {
  const { src, loading, failed } = useSceneThumbnail(sceneId, hasThumbnail);

  return (
    <div className="flex h-28 items-center justify-center bg-ink-50">
      {src ? (
        <img src={src} alt={label} className="h-full w-full object-contain" />
      ) : loading ? (
        <Icon name="Loader2" size={18} className="animate-spin text-ink-400" />
      ) : (
        <div className="flex flex-col items-center gap-1 text-ink-400">
          <Icon name={failed ? 'ImageOff' : 'LayoutTemplate'} size={20} />
          <span className="text-[10px]">{failed ? 'Vorschau nicht ladbar' : 'Kein Thumbnail'}</span>
        </div>
      )}
    </div>
  );
}
